/**
 * Relay Service
 *
 * Handles gas-free relayed withdrawals via the DKG node relayer.
 * Users can withdraw without paying gas — the relayer pays gas and gets
 * reimbursed from the withdrawal amount.
 *
 * Three withdrawal modes:
 * - Direct: User pays gas (existing flow, no relayer involved)
 * - Gas-Free: Relayer pays gas, same token delivered to recipient
 * - Swap: Relayer pays gas + NEAR Intents swaps to a different token
 */

import { ethers } from 'ethers';
import multiTokenTreasuryService from './multiTokenTreasuryService';
import MultiTokenTreasuryABI from '../contracts/MultiTokenTreasury.json';

// Per-chain minimum gas price floors matching the relayer's per-chain min tip.
// Polygon requires >= 25 gwei; others are much cheaper.
const POLYGON_CHAIN_IDS = [137, 80001, 80002];
const BNB_CHAIN_IDS = [56, 97];

function getMinGasPrice(chainId) {
    const id = Number(chainId);
    if (POLYGON_CHAIN_IDS.includes(id)) return ethers.utils.parseUnits('30', 'gwei');
    if (BNB_CHAIN_IDS.includes(id)) return ethers.utils.parseUnits('3', 'gwei');
    return ethers.utils.parseUnits('1', 'gwei'); // Sepolia, Base, Hyperliquid
}

// Relay transactions use more gas than direct withdrawals (~118k direct vs ~250k relay)
// due to signature verification, merkle proof, nullifier storage, fee splitting, and
// the 50k relayGasOverhead added by the contract.
const RELAY_GAS_ESTIMATE = 250000;

// Cache protocol fee per chain to avoid repeated RPC calls
const protocolFeeCache = {};

/**
 * Read protocolFeeBasisPoints directly from the on-chain contract.
 * Caches the result per chainId for the session.
 */
async function getProtocolFeeBpsFromContract(chainId) {
    if (protocolFeeCache[chainId] !== undefined) return protocolFeeCache[chainId];
    try {
        const numChainId = Number(chainId);
        const rpcUrl = multiTokenTreasuryService.getRpcUrlForChain(numChainId)
            || multiTokenTreasuryService.getRpcUrlForChain(String(chainId));
        const treasuryAddress = multiTokenTreasuryService.getContractAddressForChain(numChainId)
            || multiTokenTreasuryService.getContractAddressForChain(String(chainId));
        if (!rpcUrl || !treasuryAddress) return null;
        const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
        const contract = new ethers.Contract(treasuryAddress, MultiTokenTreasuryABI.abi || MultiTokenTreasuryABI, provider);
        const bps = await contract.protocolFeeBasisPoints();
        const value = bps.toNumber();
        protocolFeeCache[chainId] = value;
        return value;
    } catch (e) {
        console.warn('Failed to read protocolFeeBasisPoints from contract:', e.message);
        return null;
    }
}

const DKG_NODE_URLS = [
    process.env.REACT_APP_DKG_NODE_1 || 'http://localhost:8081',
    process.env.REACT_APP_DKG_NODE_2 || 'http://localhost:8082',
    process.env.REACT_APP_DKG_NODE_3 || 'http://localhost:8083',
    process.env.REACT_APP_DKG_NODE_4 || 'http://localhost:8084',
    process.env.REACT_APP_DKG_NODE_5 || 'http://localhost:8085',
];

/**
 * Fetch relay info from a DKG node
 * Returns relayer address, supported chains, fee info
 */
async function getRelayInfo() {
    for (const nodeUrl of DKG_NODE_URLS) {
        try {
            const response = await fetch(`${nodeUrl}/api/v2/relay/info`);
            if (!response.ok) continue;
            const data = await response.json();
            if (data.success && data.info && data.info.enabled) {
                return { ...data.info, nodeUrl };
            }
        } catch (error) {
            console.warn(`Relay info error from ${nodeUrl}:`, error.message);
        }
    }
    return null;
}

/**
 * Calculate a reasonable maxRelayerFee based on relay info
 *
 * For ETH: estimate gas cost * 1.5 buffer + protocol fee
 * For ERC20: just the protocol fee (gas reimbursed in ETH separately)
 *
 * @param {Object} relayInfo - From getRelayInfo()
 * @param {string} amount - Withdrawal amount in wei
 * @param {boolean} isNative - Whether withdrawing native token (ETH)
 * @returns {string} maxRelayerFee in wei
 */
function estimateMaxRelayerFee(relayInfo, amount, isNative) {
    const amountBN = ethers.BigNumber.from(amount);

    // Protocol fee: amount * feeBps / 10000
    const feeBps = relayInfo?.protocol_fee_bps ?? 50;
    const protocolFee = amountBN.mul(feeBps).div(10000);

    if (isNative) {
        // For native ETH: protocol fee + estimated gas reimbursement
        const maxGasPriceGwei = relayInfo?.max_gas_price_gwei || 100;
        const estimatedGas = RELAY_GAS_ESTIMATE;
        const gasReimbursement = ethers.BigNumber.from(estimatedGas)
            .mul(ethers.utils.parseUnits(String(maxGasPriceGwei), 'gwei'));

        // Add 50% buffer
        const totalWithBuffer = protocolFee.add(gasReimbursement).mul(150).div(100);
        return totalWithBuffer.toString();
    } else {
        // For ERC20: protocol fee (gas reimbursed in ETH separately)
        // Add 20% buffer
        const feeWithBuffer = protocolFee.mul(120).div(100);
        // Ensure a minimum fee so the relay node can accept the request.
        // When protocol_fee_bps is 0, fee would be 0 and the relay pre-flight
        // reverts because the relay's actual gas charge exceeds maxRelayerFee.
        // Minimum: 5% of amount (e.g., 0.1 USDC on a 2 USDC withdrawal)
        const minFee = amountBN.mul(5).div(100);
        const finalFee = feeWithBuffer.gt(minFee) ? feeWithBuffer : minFee;
        return finalFee.toString();
    }
}

/**
 * Estimate the REALISTIC relay fee for UI warnings (not the contract cap).
 * Uses the actual on-chain gas price when available, since the contract
 * charges actual tx.gasprice, not the maxRelayerFee cap.
 *
 * @param {object} relayInfo - Relay info from backend
 * @param {string} amount - Amount in wei
 * @param {boolean} isNative - Whether this is native ETH (not ERC20)
 * @param {BigNumber} [currentGasPrice] - Actual gas price from the chain (ethers BigNumber in wei)
 * @param {number|string} [chainId] - Chain ID for per-chain gas price floor
 */
function estimateRealisticRelayFee(relayInfo, amount, isNative, currentGasPrice, chainId) {
    const amountBN = ethers.BigNumber.from(amount);
    const feeBps = relayInfo?.protocol_fee_bps ?? 50;
    const protocolFee = amountBN.mul(feeBps).div(10000);

    if (isNative) {
        const estimatedGas = RELAY_GAS_ESTIMATE;
        const minGasPrice = getMinGasPrice(chainId);
        let gasReimbursement;

        if (currentGasPrice) {
            const buffered = currentGasPrice.mul(150).div(100);
            const effective = buffered.gt(minGasPrice) ? buffered : minGasPrice;
            gasReimbursement = ethers.BigNumber.from(estimatedGas).mul(effective);
        } else {
            gasReimbursement = ethers.BigNumber.from(estimatedGas).mul(minGasPrice);
        }

        const total = protocolFee.add(gasReimbursement);
        return total.toString();
    } else {
        return protocolFee.mul(120).div(100).toString();
    }
}

/**
 * Estimate fee breakdown for relay withdrawal UI display.
 * Returns human-readable values for protocol fee, gas reimbursement, and net amount.
 *
 * @param {object} relayInfo - From getRelayInfo()
 * @param {string} denominationWei - Key denomination in smallest unit
 * @param {string} denomination - Human-readable denomination (e.g. "0.1")
 * @param {boolean} isNative - Whether this is native ETH (gas deducted from amount)
 * @param {BigNumber} [gasPrice] - Actual gas price from chain (wei)
 * @param {number} [tokenDecimals=18] - Token decimals (e.g. 6 for USDC, 18 for ETH)
 * @param {number|string} [chainId] - Chain ID for per-chain gas price floor
 * @returns {{ protocolFeeBps, protocolFee, gasReimbursement, totalFee, netAmount, feePercent, viable }}
 */
function estimateFeeBreakdown(relayInfo, denominationWei, denomination, isNative, gasPrice, tokenDecimals = 18, chainId) {
    const amountBN = ethers.BigNumber.from(denominationWei);
    const feeBps = relayInfo?.protocol_fee_bps ?? 50;
    const protocolFee = amountBN.mul(feeBps).div(10000);
    const decimals = tokenDecimals || 18;

    const estimatedGas = RELAY_GAS_ESTIMATE;
    let gasReimbursement = ethers.BigNumber.from(0);

    if (isNative) {
        // Use actual gas price × 1.5 buffer for relayer's EIP-1559 markup.
        // Floor at per-chain minimum (relayer enforces min tip per chain).
        const minGasPrice = getMinGasPrice(chainId);
        let effectiveGasPrice;
        if (gasPrice) {
            const buffered = gasPrice.mul(150).div(100);
            effectiveGasPrice = buffered.gt(minGasPrice) ? buffered : minGasPrice;
        } else {
            effectiveGasPrice = minGasPrice;
        }

        gasReimbursement = ethers.BigNumber.from(estimatedGas).mul(effectiveGasPrice);
    }

    const totalFee = protocolFee.add(gasReimbursement);
    const netAmount = amountBN.gt(totalFee) ? amountBN.sub(totalFee) : ethers.BigNumber.from(0);
    const viable = amountBN.gt(totalFee);

    // Calculate fee percentage
    const feePercent = amountBN.gt(0)
        ? parseFloat(ethers.utils.formatUnits(totalFee, decimals)) / parseFloat(denomination) * 100
        : 0;

    return {
        protocolFeeBps: feeBps,
        protocolFee: ethers.utils.formatUnits(protocolFee, decimals),
        gasReimbursement: isNative ? ethers.utils.formatUnits(gasReimbursement, decimals) : '0',
        totalFee: ethers.utils.formatUnits(totalFee, decimals),
        netAmount: ethers.utils.formatUnits(netAmount, decimals),
        feePercent: feePercent.toFixed(1),
        viable,
        isNative,
    };
}

/**
 * Estimate fee breakdown for Solana relay withdrawals.
 * Solana uses a fixed relayer fee (in lamports) + protocol fee (bps), not gas-based.
 *
 * @param {number} protocolFeeBps - From on-chain treasury state
 * @param {string} fixedRelayerFeeLamports - From on-chain treasury state
 * @param {string} denominationLamports - Key denomination in lamports
 * @param {string} denomination - Human-readable denomination (e.g. "0.1")
 * @param {number} [tokenDecimals=9] - Token decimals (9 for SOL, 6 for USDC-SPL, etc.)
 * @returns {{ protocolFeeBps, protocolFee, relayerFee, totalFee, netAmount, feePercent, viable, isSolana }}
 */
function estimateSolanaFeeBreakdown(protocolFeeBps, fixedRelayerFeeLamports, denominationLamports, denomination, tokenDecimals = 9) {
    const amountBN = ethers.BigNumber.from(denominationLamports);
    const feeBps = protocolFeeBps ?? 0;
    const protocolFee = amountBN.mul(feeBps).div(10000);
    const relayerFee = ethers.BigNumber.from(fixedRelayerFeeLamports || '0');
    const decimals = tokenDecimals || 9;

    const totalFee = protocolFee.add(relayerFee);
    const netAmount = amountBN.gt(totalFee) ? amountBN.sub(totalFee) : ethers.BigNumber.from(0);
    const viable = amountBN.gt(totalFee);

    const feePercent = amountBN.gt(0)
        ? parseFloat(ethers.utils.formatUnits(totalFee, decimals)) / parseFloat(denomination) * 100
        : 0;

    return {
        protocolFeeBps: feeBps,
        protocolFee: ethers.utils.formatUnits(protocolFee, decimals),
        relayerFee: ethers.utils.formatUnits(relayerFee, decimals),
        totalFee: ethers.utils.formatUnits(totalFee, decimals),
        netAmount: ethers.utils.formatUnits(netAmount, decimals),
        feePercent: feePercent.toFixed(1),
        viable,
        isSolana: true,
    };
}

export {
    getRelayInfo,
    getProtocolFeeBpsFromContract,
    estimateMaxRelayerFee,
    estimateRealisticRelayFee,
    estimateFeeBreakdown,
    estimateSolanaFeeBreakdown,
};
